import React, { useEffect, useMemo, useState, ReactNode } from "react";

// UX
import CardDFInterfaceUX from "./CardDFInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import EditorColumn from "react-lib/appcon/common/EditorColumn";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox, Icon } from "semantic-ui-react";
import formatComma from "react-lib/utils/formatComma";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SelectedItemsType,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/DFInterface";

// Serializer
import DFInterfaceSerializer from "issara-sdk/types/DFInterfaceSerializer_apps_INF";

// Type
type CardDFInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  DFInterfaceSequence: State["DFInterfaceSequence"];
  languageUX: any;
} & PickedProps;

const CardDFInterface = (props: CardDFInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedDF, setSelectedDF] = useState<SelectedItemsType[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "DFInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  // isChecked
  const isChecked = (id: number) => selectedDF.some((s) => s.id === id);

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      const newData = (props.DFInterfaceSequence?.dfInterfaceList || []).map(
        (item: DFInterfaceSerializer) => ({
          id: item.id,
          df_type: item.df_type,
          sel_date: item.sel_date,
          per_unit: item.per_unit,
          per_na: item.per_na,
          req_unit: item.req_unit,
          req_na: item.req_na,
        })
      );

      setSelectedDF(newData);
    } else {
      setSelectedDF([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "DFInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedDF([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "DFInterface",
      action: "SAVE",
      selectedItems: selectedDF,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedDF([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "DFInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedDF([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`DFInterfaceSequence.searchDF.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`DFInterfaceSequence.searchDF.createdDate`, date);
  };

  const handleChangeEdit = (id: number, key: keyof SelectedItemsType, value: any) => {
    setSelectedDF((prev) => {
      const exists = prev.find((s) => s.id === id);

      if (exists) {
        return prev.map((s) => (s.id === id ? { ...s, [key]: value } : s));
      } else {
        const originalItem = props.DFInterfaceSequence?.dfInterfaceList?.find((i) => i.id === id);

        if (!originalItem) return prev;

        return [
          ...prev,
          {
            ...originalItem,
            [key]: value,
          },
        ];
      }
    });
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`DFInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "DFInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedDF([]);
    props.setProp(`DFInterfaceSequence.selectedItems`, []);
  };

  // const handleDisabledAdd = useMemo(() => {
  //   if (selectedDF.length === 0) return true;

  //   return !selectedDF.some((selectedRow) => {
  //     const originalRow = props.DFInterfaceSequence?.dfInterfaceList?.find(
  //       (item: DFInterfaceSerializer) => item.id === selectedRow.id
  //     );

  //     if (!originalRow) return false;

  //     const isNotSent = originalRow.sent === false;

  //     return isNotSent;
  //   })
  // }, [selectedDF, props.DFInterfaceSequence?.dfInterfaceList]);

  // const handleDisabledEdit = useMemo(() => {
  //   if (selectedDF.length === 0) return true;

  //   return !selectedDF.some((selectedRow) => {
  //     const originalRow = props.DFInterfaceSequence?.dfInterfaceList?.find(
  //       (item: DFInterfaceSerializer) => item.id === selectedRow.id
  //     );

  //     if (!originalRow) return false;

  //     const isEditted =
  //       selectedRow.df_type !== originalRow.df_type ||
  //       selectedRow.sel_date !== originalRow.sel_date ||
  //       selectedRow.per_unit !== originalRow.per_unit ||
  //       selectedRow.req_unit !== originalRow.req_unit ||
  //       selectedRow.per_na !== originalRow.req_na ||
  //       selectedRow.req_na !== originalRow.req_na;

  //     return isEditted;
  //   });
  // }, [selectedDF, props.DFInterfaceSequence?.dfInterfaceList]);

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedDF.length === 0) return true;
  }, [selectedDF, props.DFInterfaceSequence?.dfInterfaceList]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const columnTablePrice = (data: any, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "flex-end" }}>{data ?? "0.00"}</div>
    );
  };

  const columnTableIconCheck = (data: boolean, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : (
          <Icon name="times circle" color="red" size="big" />
        )}
      </div>
    );
  };

  // EditorColumn Usage
  const renderEditorColumn = (id: number, field: keyof SelectedItemsType, value: any) => (
    <EditorColumn
      disabled={!isChecked(id)}
      value={value}
      valueOnFocus={true}
      backgroundColor={"rgb(255, 255, 204)"}
      onDataChanged={(v: string) => handleChangeEdit(id, field, v)}
    />
  );

  // useMemo
  const dfInterfaceList = useMemo(() => {
    return (props.DFInterfaceSequence?.dfInterfaceList || []).map(
      (item: DFInterfaceSerializer, index: number) => {
        let createdName = (props.DFInterfaceSequence?.userOptions || []).find(
          (data: DFInterfaceSerializer) => data?.id === item.created_by
        );

        const selectedRow = selectedDF.find((s) => s.id === item.id);
        const selDateValue = selectedRow?.sel_date || item.sel_date;
        const dfTypeValue = selectedRow?.df_type || item.df_type;
        const perUnitValue = selectedRow?.per_unit || item.per_unit;
        const reqUnitValue = selectedRow?.req_unit || item.req_unit;
        const perNaValue = selectedRow?.per_na || item.per_na;
        const reqNaValue = selectedRow?.req_na || item.req_na;

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={isChecked(item.id!)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  const exists = selectedDF.find((s) => s.id === item.id);
                  if (!exists) {
                    setSelectedDF([
                      ...selectedDF,
                      {
                        id: item.id,
                        df_type: item.df_type,
                        sel_date: item.sel_date,
                        per_unit: item.per_unit,
                        per_na: item.per_na,
                        req_unit: item.per_unit,
                        req_na: item.req_na,
                      },
                    ]);
                  }
                } else {
                  setSelectedDF(selectedDF.filter((s) => s.id !== item.id));
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DFInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created_at") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else if (["valid", "sent"].includes(key)) {
                data[key] = columnTableIconCheck(!!item[key]);
              } else if (key === "created_by") {
                data[key] = columnTableCenter(
                  (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
                );
              } else if (priceColumns.includes(key)) {
                data[key] = columnTablePrice(
                  typeof item[key] === "string" || typeof item[key] === "number"
                    ? formatComma(item[key])
                    : item[key]
                );
              } else if (centerColumns.includes(key)) {
                data[key] = columnTableCenter(item[key]);
              } else {
                data[key] = item[key];
              }

              return data;
            },
            {}
          ),
          // * Note: sel_date อาจจะต้องแก้ Component ให้ตรงกับเลือกวันที่, df_type, per_unit, req_unit, per_na, req_na อาจจะต้องใช้แบบ options
          sel_date: renderEditorColumn(item.id!, "sel_date", selDateValue),
          df_type: renderEditorColumn(item.id!, "df_type", dfTypeValue),
          per_unit: renderEditorColumn(item.id!, "per_unit", perUnitValue),
          req_unit: renderEditorColumn(item.id!, "req_unit", reqUnitValue),
          per_na: renderEditorColumn(item.id!, "per_na", perNaValue),
          req_na: renderEditorColumn(item.id!, "req_na", reqNaValue),
        };
      }
    );
  }, [
    selectedDF,
    props.DFInterfaceSequence?.dfInterfaceList,
    props.DFInterfaceSequence?.userOptions,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardDFInterfaceUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDF={props.DFInterfaceSequence?.searchDF}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedDF.length === (props.DFInterfaceSequence?.dfInterfaceList || []).length &&
                  (props.DFInterfaceSequence?.dfInterfaceList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        dfInterfaceList={dfInterfaceList}
        paginatedItems={props.DFInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        addButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("ADD")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_ADD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_ADD`]}
            // config
            color="blue"
            title="Add Record"
            name="ADD"
            size="medium"
          />
        }
        editButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("EDIT")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_EDIT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_EDIT`]}
            // config
            color="green"
            title="Edit Date"
            name="EDIT"
            size="medium"
          />
        }
      />
    </>
  );
};

CardDFInterface.displayName = "CardDFInterface";

export default React.memo(CardDFInterface);

const priceColumns: Partial<keyof DFInterfaceSerializer>[] = ["amt", "price", "compensate"];

const centerColumns: Partial<keyof DFInterfaceSerializer>[] = [
  "id",
  "_id",
  "version",
  "invoice_item_status",
  "data_type",
  "rec_date",
  "rec_no",
  "hn",
  "service_id",
  "qty",
  "per_unit_old",
  "per_date",
  "per_time",
  "perf_pid",
  "perf_na",
  "perf_role",
  "role_code",
  "product_id",
  "sac_type",
  "bil_doc_no",
  "sithi",
  "frmtbl",
  "obs_type",
  "obs_id",
  "obs_id",
  "status",
  "bill_type",
  "doctor_fee_order",
  "service_compensation_item",
  "invoice_item",
];

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 160,
  version: 80,
};

const tableHeaders = GenerateTableHeader(columnWidths);
